import api from "lib/axios";
import type { ApiEnvelope, PaginatedResult } from "services/apiTypes";
import { unwrapData, unwrapList } from "services/apiTypes";

export type CourseFolder = {
  id: number;
  course_id?: number;
  parent_id?: number | null;
  title: string;
  is_locked?: boolean;
  sort_order?: number;
  children_count?: number;
  items_count?: number;
  created_at?: string;
  updated_at?: string;
};

// Internal folder used as the course root for items that are not inside a
// user-created folder. It must never appear as a visible folder or be counted
// in course statistics.
export const SYSTEM_ROOT_FOLDER_TITLE = "__ROOT__";

export type FolderUpsertBody = {
  title: string;
  parent_id?: number | null;
  is_locked?: boolean;
};

type FolderCreateOptions = {
  silent?: boolean;
};

export type FolderMoveBody = {
  parent_id?: number | null;
  sort_order?: number | null;
};

export type FolderReorderRow = {
  id: number;
  sort_order: number;
  parent_id?: number | null;
};

export const instructorCourseFoldersService = {
  async list(courseId: number | string, params: { parent_id?: number | null } = {}) {
    const res = await api.get<
      ApiEnvelope<PaginatedResult<CourseFolder>> | ApiEnvelope<{ data: CourseFolder[] }>
    >(`/instructors/courses/${courseId}/folders`, {
      params,
      meta: { toastSuccess: false },
    });

    const paginated = unwrapList<CourseFolder>(res.data as any);
    if (Array.isArray(paginated.data) && (paginated.meta?.total ?? 0) >= 0) return paginated;

    const list = (res.data as any)?.result?.data;
    return {
      data: Array.isArray(list) ? list : [],
      meta: {
        current_page: 1,
        last_page: 1,
        per_page: 50,
        total: Array.isArray(list) ? list.length : 0,
        from: null,
        to: null,
      },
    } as PaginatedResult<CourseFolder>;
  },

  async get(courseId: number | string, folderId: number | string) {
    const res = await api.get<ApiEnvelope<{ data: CourseFolder }>>(
      `/instructors/courses/${courseId}/folders/${folderId}`,
      { meta: { toastSuccess: false } }
    );
    return unwrapData(res.data);
  },

  async create(
    courseId: number | string,
    body: FolderUpsertBody,
    options: FolderCreateOptions = {}
  ) {
    const res = await api.post<ApiEnvelope<{ data: CourseFolder }>>(
      `/instructors/courses/${courseId}/folders`,
      body,
      options.silent ? { meta: { toast: false } } : undefined
    );
    return unwrapData(res.data);
  },

  async update(courseId: number | string, folderId: number | string, body: FolderUpsertBody) {
    const res = await api.put<ApiEnvelope<{ data: CourseFolder }>>(
      `/instructors/courses/${courseId}/folders/${folderId}`,
      body
    );
    return unwrapData(res.data);
  },

  async remove(courseId: number | string, folderId: number | string) {
    const res = await api.delete<ApiEnvelope<unknown>>(
      `/instructors/courses/${courseId}/folders/${folderId}`
    );
    return { message: (res.data as any)?.message ?? "Deleted." };
  },

  /**
   * Move a folder to a new parent and/or sort position.
   * Success toast is suppressed by default because the UI shows its own feedback.
   */
  async move(courseId: number | string, folderId: number | string, body: FolderMoveBody) {
    const res = await api.post<ApiEnvelope<{ data: CourseFolder }>>(
      `/instructors/courses/${courseId}/folders/${folderId}/move`,
      body,
      { meta: { toastSuccess: false } }
    );
    return unwrapData(res.data);
  },

  /**
   * Bulk reorder folders. Silent by default.
   */
  async reorder(courseId: number | string, items: FolderReorderRow[]) {
    await api.post<ApiEnvelope<unknown>>(
      `/instructors/courses/${courseId}/folders/reorder`,
      { items },
      { meta: { toast: false } }
    );
  },
};
